import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpErrorResponse } from '@angular/common/http';
import { LoginService } from '@services/login/login.service';
import { Router } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
  private readonly EXCLUDED_URLS = ['/auth/login', '/auth/register'];

  constructor(
    private loginService: LoginService,
    private router: Router
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    // 1️⃣ Si la URL es de login o registro, la dejamos pasar sin modificar.
    if (this.shouldExcludeUrl(req.url)) {
      return next.handle(req);
    }

    // 2️⃣ Si el usuario aún no está autenticado, dejamos pasar la solicitud sin modificar.
    if (!this.loginService.isAuthenticated()) {
      return next.handle(req);
    }

    // Obtener el token
    const token = this.loginService.getToken();

    // Si no hay token, continuamos sin modificar la solicitud.
    if (!token) {
      return next.handle(req);
    }

    if (!token || this.isTokenExpired(token)) {
      this.loginService.logout();
      this.router.navigate(['/login']);
      return next.handle(req);
    }

    // Ahora sí obtenemos el IdEmpresa
    const idEmpresa = this.getCachedIdEmpresa(token);

    // Clonamos la solicitud agregando los headers necesarios
    const modifiedRequest = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(idEmpresa && { 'X-IdEmpresa': idEmpresa }) // Agregar solo si existe
      }
    });

    return next.handle(modifiedRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        // Manejo de errores
        if (error.status === 401) {
          this.loginService.logout();
          this.router.navigate(['/login']);
        }
        return throwError(() => error);
      })
    );
  }

  private isTokenExpired(token: string): boolean {
    const payload = this.decodeJwtPayload(token);
    if (!payload || !payload.exp) return true;

    const expirationDate = new Date(payload.exp * 1000);  // Exp es en segundos, convertir a milisegundos
    return expirationDate <= new Date();
  }

  private shouldExcludeUrl(url: string): boolean {
    return this.EXCLUDED_URLS.some(excluded => url.includes(excluded));
  }

  private getCachedIdEmpresa(token: string): string | null {
    const cachedIdEmpresa = localStorage.getItem('IdEmpresa');
    if (cachedIdEmpresa) return cachedIdEmpresa;

    const idEmpresa = this.getIdEmpresaFromToken(token);
    if (idEmpresa) localStorage.setItem('IdEmpresa', idEmpresa);

    return idEmpresa;
  }

  private getIdEmpresaFromToken(token: string): string | null {
    try {
      const payload = this.decodeJwtPayload(token);
      return payload?.IdEmpresa || null;
    } catch (error) {
      console.error('Error al obtener IdEmpresa del token:', error);
      return null;
    }
  }

  private decodeJwtPayload(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error al decodificar el token:', error);
      return null;
    }
  }
}
